import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { fr } from "date-fns/locale";

export const STATUSES = [
  { id: "idee", label: "Idée", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  { id: "a_creer", label: "À créer", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "en_cours", label: "Création en cours", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "a_valider", label: "À faire valider", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "modification", label: "Modification demandée", cls: "bg-red-50 text-red-700 border-red-200" },
  { id: "valide", label: "Validé", cls: "bg-green-50 text-green-700 border-green-200" },
  { id: "programme", label: "Programmé", cls: "bg-green-500 text-white border-green-500" },
  { id: "publication_en_cours", label: "Publication en cours", cls: "bg-blue-500 text-white border-blue-500" },
  { id: "publie", label: "Publié", cls: "bg-green-500 text-white border-green-500" },
  { id: "erreur_publication", label: "Erreur de publication", cls: "bg-red-500 text-white border-red-500" },
  { id: "annulee", label: "Annulée", cls: "bg-neutral-200 text-neutral-500 border-neutral-300" },
];

export const PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "tiktok", label: "TikTok" },
  { id: "pinterest", label: "Pinterest" },
];

export const FORMATS = [
  { id: "reel", label: "Reel" },
  { id: "carrousel", label: "Carrousel" },
  { id: "photo", label: "Photo" },
  { id: "story", label: "Story" },
  { id: "post", label: "Post simple" },
  { id: "citation", label: "Citation" },
  { id: "avant_apres", label: "Avant/après" },
  { id: "temoignage", label: "Témoignage" },
];

export const OBJECTIVES = [
  { id: "engagement", label: "Engagement" },
  { id: "notoriete", label: "Notoriété" },
  { id: "vente", label: "Vente" },
  { id: "education", label: "Éducation" },
  { id: "acquisition", label: "Acquisition" },
  { id: "fidelisation", label: "Fidélisation" },
  { id: "interaction", label: "Interaction" },
  { id: "trafic", label: "Trafic site web" },
];

export const CLIENT_COLORS = ["#395963", "#E8B931", "#B5A08A", "#7C6AAE", "#C96F5A", "#4F7E5B", "#3E6B8E", "#A85B78"];

export const statusOf = (id) => STATUSES.find((s) => s.id === id) || STATUSES[1];

export const isVideo = (url) => /\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(url || "");
export const platformLabel = (id) => PLATFORMS.find((p) => p.id === id)?.label || id;
export const formatLabel = (id) => FORMATS.find((f) => f.id === id)?.label || id;
export const objectiveLabel = (id) => OBJECTIVES.find((o) => o.id === id)?.label || id;

export function frDate(iso) {
  if (!iso) return "";
  try {
    const dt = parseISO(iso);
    if (isToday(dt)) return "Aujourd'hui";
    if (isTomorrow(dt)) return "Demain";
    return format(dt, "d MMMM", { locale: fr });
  } catch { return iso; }
}

export function frDateLong(iso) {
  if (!iso) return "";
  try { return format(parseISO(iso), "EEEE d MMMM yyyy", { locale: fr }); } catch { return iso; }
}

export const TASK_STATUSES = [
  { id: "a_faire", label: "À faire", dot: "bg-blue-500", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "programme", label: "Programmé", dot: "bg-indigo-500", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { id: "en_cours", label: "En cours", dot: "bg-yellow-400", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { id: "envoye_validation", label: "Envoyé pour validation", dot: "bg-orange-400", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "livre_client", label: "Livré au client", dot: "bg-green-500", cls: "bg-green-50 text-green-700 border-green-200" },
  { id: "finalise", label: "Finalisé", dot: "bg-green-700", cls: "bg-green-100 text-green-800 border-green-300" },
];

export const taskStatusOf = (id) => {
  if (id === "termine") return TASK_STATUSES.find((s) => s.id === "finalise");
  return TASK_STATUSES.find((s) => s.id === id) || TASK_STATUSES[0];
};

export const TIME_CATEGORIES = [
  { id: "community", label: "Community Management" },
  { id: "graphisme", label: "Graphisme" },
  { id: "contenu", label: "Création de contenu" },
  { id: "video", label: "Montage vidéo" },
  { id: "web", label: "Site web" },
  { id: "seo", label: "SEO" },
  { id: "ads", label: "Publicité / Ads" },
  { id: "reunion", label: "Réunion client" },
  { id: "admin", label: "Administratif" },
  { id: "autre", label: "Autre" },
];

export const timeCategoryLabel = (id) => TIME_CATEGORIES.find((c) => c.id === id)?.label || id;

export const BILLING_TYPES = [
  { id: "forfait", label: "Forfait mensuel" },
  { id: "projet", label: "Projet ponctuel" },
  { id: "horaire", label: "Facturation horaire" },
];

export const billingTypeLabel = (id) => BILLING_TYPES.find((b) => b.id === id)?.label || "—";

export function fmtDur(sec) {
  sec = Math.round(sec || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m} min`;
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

export function fmtHMS(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

export function fmtEuro(n) {
  return `${(Math.round(n * 100) / 100).toLocaleString("fr-FR")} €`;
}

export const CONTENT_REQUEST_TYPES = [
  { id: "photo", label: "Photo" },
  { id: "video", label: "Vidéo" },
  { id: "logo", label: "Logo" },
  { id: "document", label: "Document" },
  { id: "texte", label: "Information textuelle" },
  { id: "prix", label: "Prix" },
  { id: "offre", label: "Offre commerciale" },
  { id: "horaires", label: "Horaires" },
  { id: "temoignage", label: "Témoignage" },
  { id: "produit", label: "Produit" },
  { id: "autre", label: "Autre" },
];

export const contentTypeLabel = (id) =>
  CONTENT_REQUEST_TYPES.find((t) => t.id === id)?.label || id;

export const CONTENT_REQUEST_STATUSES = [
  { id: "a_fournir", label: "À fournir", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  { id: "partiel", label: "Partiellement reçu", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { id: "recu", label: "Reçu", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "en_retard", label: "En retard", cls: "bg-red-50 text-red-700 border-red-200" },
  { id: "valide", label: "Validé", cls: "bg-green-50 text-green-700 border-green-200" },
];

export const contentRequestStatus = (id) =>
  CONTENT_REQUEST_STATUSES.find((s) => s.id === id) || CONTENT_REQUEST_STATUSES[0];

export const ITEM_STATE_META = {
  en_attente: { icon: "❌", label: "Manquant" },
  partiel: { icon: "⏳", label: "Partiel" },
  recu: { icon: "✅", label: "Reçu" },
};

export const CLIENT_REQUEST_TYPES = [
  { id: "nouvelle_publication", label: "Nouvelle publication" },
  { id: "modification_publication", label: "Modification d'une publication" },
  { id: "evenement", label: "Événement à communiquer" },
  { id: "offre", label: "Offre commerciale" },
  { id: "nouveau_produit", label: "Nouveau produit/service" },
  { id: "info", label: "Information importante" },
  { id: "urgente", label: "Modification urgente" },
  { id: "ads", label: "Campagne publicitaire" },
  { id: "newsletter", label: "Newsletter" },
  { id: "shooting", label: "Shooting" },
  { id: "creation_graphique", label: "Création graphique" },
  { id: "autre", label: "Autre" },
];

export const clientRequestTypeLabel = (id) =>
  CLIENT_REQUEST_TYPES.find((t) => t.id === id)?.label || id;

export const CLIENT_REQUEST_STATUSES = [
  { id: "nouvelle", label: "Nouvelle", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "acceptee", label: "Acceptée", cls: "bg-green-50 text-green-700 border-green-200" },
  { id: "devis_envoye", label: "Devis envoyé", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { id: "devis_accepte", label: "Devis accepté", cls: "bg-green-50 text-green-700 border-green-200" },
  { id: "devis_refuse", label: "Devis refusé", cls: "bg-red-50 text-red-700 border-red-200" },
  { id: "refusee", label: "Refusée", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
  { id: "terminee", label: "Terminée", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
];

export const clientRequestStatus = (id) =>
  CLIENT_REQUEST_STATUSES.find((s) => s.id === id) || CLIENT_REQUEST_STATUSES[0];

// Services d'une demande (Kanban Demandes) — pastels DGA, réutilisés partout
export const DEMANDE_SERVICES = [
  { id: "community", label: "Community", full: "Community Management", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "graphisme", label: "Graphisme", full: "Graphisme", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "web", label: "Web Design", full: "Web Design", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { id: "conseil", label: "Conseil", full: "Conseil", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "autre", label: "Autre", full: "Autre", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
];
export const demandeService = (id) => DEMANDE_SERVICES.find((s) => s.id === id) || DEMANDE_SERVICES[4];

export const KANBAN_COLS = [
  { id: "nouvelle", label: "Nouvelle", head: "bg-emerald-50/80 border-emerald-100", ring: "ring-emerald-300" },
  { id: "a_traiter", label: "À traiter", head: "bg-rose-50/80 border-rose-100", ring: "ring-rose-300" },
  { id: "en_cours", label: "En cours", head: "bg-blue-50/80 border-blue-100", ring: "ring-blue-300" },
  { id: "a_valider", label: "À valider", head: "bg-amber-50/80 border-amber-100", ring: "ring-amber-300" },
  { id: "finalisee", label: "Finalisée", head: "bg-green-50/80 border-green-100", ring: "ring-green-300" },
];
export const kanbanCol = (id) => KANBAN_COLS.find((c) => c.id === id) || KANBAN_COLS[0];

export const DEMANDE_PRIORITIES = {
  haute: { label: "Haute", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  moyenne: { label: "Moyenne", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  basse: { label: "Basse", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
export const demandePriority = (id) => DEMANDE_PRIORITIES[id] || DEMANDE_PRIORITIES.moyenne;


export const QUOTE_STATUSES = {
  envoye: { label: "En attente de réponse", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  accepte: { label: "Accepté", cls: "bg-green-50 text-green-700 border-green-200" },
  refuse: { label: "Refusé", cls: "bg-red-50 text-red-700 border-red-200" },
};

export const SERVICE_TYPES = [
  { id: "communication", label: "Communication digitale" },
  { id: "graphisme", label: "Graphisme" },
  { id: "les_deux", label: "Communication digitale + Graphisme" },
];

// Prestations cumulables — un client = une fiche, plusieurs prestations
export const PRESTATIONS = [
  { id: "community_management", label: "Community management" },
  { id: "graphisme", label: "Graphisme" },
  { id: "site_web", label: "Site web" },
  { id: "seo", label: "SEO" },
  { id: "publicite", label: "Publicité Meta Ads / Google Ads" },
  { id: "newsletter", label: "Newsletter" },
  { id: "maintenance_web", label: "Maintenance web" },
  { id: "autre", label: "Autre" },
];
export const legacyServices = (st) =>
  st === "graphisme" ? ["graphisme"] : st === "les_deux" ? ["community_management", "graphisme"] : ["community_management"];
export const prestationLabel = (id) => PRESTATIONS.find((p) => p.id === id)?.label || id;

/* Prestations effectives d'un client (services cochés, sinon déduit de l'ancien service_type) */
export const clientServices = (c) => (c?.services?.length ? c.services : legacyServices(c?.service_type));
export const isCmClient = (c) => clientServices(c).includes("community_management");
export const isGraphismeClient = (c) => clientServices(c).includes("graphisme");
export const isWebClient = (c) => clientServices(c).some((s) => ["site_web", "maintenance_web"].includes(s));

/* Visibilité feed : uniquement les publications abouties — jamais les idées,
   « à créer » ni « création en cours », jamais les stories */
export const FEED_STATUSES = new Set(["a_valider", "valide", "planifie", "programme", "publie"]);
export const isFeedVisible = (p) => FEED_STATUSES.has(p?.status) && p?.format !== "story";

/* Cache-busting des visuels : feed/calendrier/aperçus ne gardent aucune « mémoire »
   d'un visuel modifié (le fichier preview est servi en cache immutable à URL stable) */
export const bustUrl = (url, updatedAt) =>
  url && updatedAt ? `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(updatedAt)}` : url;

/* Visuel d'une publication : préfère la preview WebP ; une vidéo sans couverture
   renvoie video:true → à rendre en <video> (jamais en <img>, sinon vignette cassée) */
export const pubVisualFull = (p) => {
  if (!p) return { src: null, video: false };
  const v = p.visual_preview_url || p.visual_url || "";
  if (v && isVideo(v.split("?")[0])) {
    const cover = p.cover_preview_url || p.cover_url;
    return cover
      ? { src: bustUrl(cover, p.updated_at), video: false }
      : { src: bustUrl(v, p.updated_at), video: true };
  }
  const sp = p.slides_previews?.[0];
  let raw = v || (sp ? (typeof sp === "string" ? sp : sp.url) : "");
  if (!raw) {
    const s = p.slides?.[0];
    raw = s ? (typeof s === "string" ? s : s.url) : "";
  }
  if (!raw) raw = p.cover_preview_url || p.cover_url || "";
  return { src: raw ? bustUrl(raw, p.updated_at) : null, video: false };
};

// ---- Module Sites web ----
export const WEB_PROJECT_TYPES = [
  { id: "vitrine", label: "Création de site vitrine" },
  { id: "ecommerce", label: "Création de site e-commerce" },
  { id: "landing", label: "Landing page" },
  { id: "refonte", label: "Refonte de site" },
  { id: "fonctionnalites", label: "Développement de fonctionnalités" },
  { id: "maintenance", label: "Maintenance" },
  { id: "autre", label: "Autre" },
];
export const WEB_CMS = [
  { id: "wordpress", label: "WordPress" }, { id: "wix", label: "Wix" },
  { id: "shopify", label: "Shopify" }, { id: "webflow", label: "Webflow" },
  { id: "sur_mesure", label: "Développement sur mesure" }, { id: "autre", label: "Autre" },
];
export const WEB_STAGES = [
  { id: "creation", label: "Projet créé" }, { id: "cahier_des_charges", label: "Cahier des charges" },
  { id: "collecte", label: "Collecte des éléments" }, { id: "design", label: "Conception graphique" },
  { id: "developpement", label: "Développement" }, { id: "seo", label: "Optimisation SEO" },
  { id: "tests", label: "Tests et corrections" }, { id: "validation_finale", label: "Validation finale" },
  { id: "prete_mise_en_ligne", label: "Prêt pour mise en ligne" }, { id: "en_ligne", label: "Site en ligne" },
  { id: "recette", label: "Recette post-lancement" }, { id: "cloture", label: "Projet clôturé" },
];
export const WEB_OP_STATUSES = [
  { id: "en_cours", label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "attente_client", label: "En attente du client", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "attente_webmaster", label: "En attente du webmaster", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "attente_validation", label: "En attente de validation", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { id: "bloque", label: "Bloqué", cls: "bg-red-50 text-red-700 border-red-200" },
  { id: "en_retard", label: "En retard", cls: "bg-red-100 text-red-800 border-red-300" },
  { id: "suspendu", label: "Suspendu", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
  { id: "termine", label: "Terminé", cls: "bg-green-50 text-green-700 border-green-200" },
];
export const WEB_ELEMENT_KINDS = [
  { id: "image", label: "Image" }, { id: "video", label: "Vidéo" }, { id: "texte", label: "Texte" },
  { id: "pdf", label: "PDF" }, { id: "document", label: "Document" }, { id: "lien", label: "Lien" },
];
export const WEB_ELEMENT_STATUS = {
  a_fournir: { label: "En attente du client", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  recu: { label: "Reçu — à vérifier", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  correction_demandee: { label: "Correction demandée", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  valide: { label: "Validé", cls: "bg-green-50 text-green-700 border-green-200" },
};
export const WEB_PAGE_STATUS = {
  status_content: [{ id: "manquant", label: "Manquant" }, { id: "recu", label: "Reçu" }, { id: "valide", label: "Validé" }],
  status_design: [{ id: "a_faire", label: "À faire" }, { id: "en_cours", label: "En cours" }, { id: "valide", label: "Validée" }],
  status_dev: [{ id: "a_faire", label: "À faire" }, { id: "en_cours", label: "En cours" }, { id: "termine", label: "Terminé" }],
  status_seo: [{ id: "a_faire", label: "À faire" }, { id: "en_cours", label: "En cours" }, { id: "a_verifier", label: "À vérifier" }, { id: "valide", label: "Validé" }],
  status_validation: [{ id: "non_envoyee", label: "Non envoyée" }, { id: "envoyee", label: "Envoyée" }, { id: "valide", label: "Validée" }, { id: "modifications", label: "Modifications" }],
};
export const WEB_TASK_CATEGORIES = [
  { id: "gestion", label: "Gestion de projet" }, { id: "design", label: "Design" },
  { id: "developpement", label: "Développement" }, { id: "seo", label: "SEO" },
  { id: "corrections", label: "Corrections" }, { id: "reunions", label: "Réunions" },
  { id: "maintenance", label: "Maintenance" },
];
export const BRIEF_SECTIONS = [
  { id: "presentation", title: "Présentation de l'entreprise", questions: [
    ["activite", "Activité"], ["historique", "Historique"], ["prestations", "Prestations"],
    ["clientele", "Clientèle cible"], ["zone", "Zone géographique"], ["positionnement", "Positionnement"],
    ["differenciation", "Points différenciants"]] },
  { id: "objectifs", title: "Objectifs du site", questions: [
    ["objectifs", "Objectifs (devis, appels, RDV, vente en ligne, visibilité locale, image de marque…)"]] },
  { id: "identite", title: "Identité visuelle", questions: [
    ["logo", "Logo"], ["charte", "Charte graphique"], ["couleurs", "Couleurs"],
    ["typographies", "Typographies"], ["ambiance", "Ambiance souhaitée"], ["references", "Sites de référence"]] },
  { id: "fonctionnalites", title: "Fonctionnalités", questions: [
    ["fonctionnalites", "Formulaire de contact, blog, galerie, RDV, e-commerce, newsletter, multilingue, intégrations…"]] },
  { id: "technique", title: "Informations techniques", questions: [
    ["domaine", "Nom de domaine existant"], ["hebergement", "Hébergement existant"],
    ["cms_actuel", "CMS actuel"], ["migration", "Migration nécessaire"], ["outils", "Outils à connecter"]] },
];
export const webStageLabel = (id) => WEB_STAGES.find((s) => s.id === id)?.label || id;
export const webTypeLabel = (id) => WEB_PROJECT_TYPES.find((t) => t.id === id)?.label || id;
export const webCmsLabel = (id) => WEB_CMS.find((c) => c.id === id)?.label || id;

export const WEB_DESIGN_KINDS = [
  { id: "moodboard", label: "Moodboard" }, { id: "inspiration", label: "Inspiration" },
  { id: "palette", label: "Palette de couleurs" }, { id: "typographie", label: "Typographies" },
  { id: "maquette_desktop", label: "Maquette desktop" }, { id: "maquette_mobile", label: "Maquette mobile" },
  { id: "autre", label: "Autre" },
];
export const WEB_VERSION_STATUS = {
  brouillon: { label: "Brouillon interne", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
  envoyee: { label: "En validation client", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  validee: { label: "Validée", cls: "bg-green-50 text-green-700 border-green-200" },
  modifications: { label: "Modifications demandées", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};
export const WEB_VALIDATION_KINDS = [
  { id: "cahier_des_charges", label: "Cahier des charges" }, { id: "arborescence", label: "Arborescence" },
  { id: "identite", label: "Identité graphique" }, { id: "maquette", label: "Maquette" },
  { id: "preprod", label: "Version de préproduction" }, { id: "finale", label: "Validation finale" },
  { id: "livraison", label: "Livraison" },
];
export const WEB_ANOMALY_CATEGORIES = [
  { id: "responsive", label: "Responsive" }, { id: "navigation", label: "Navigation" },
  { id: "formulaires", label: "Formulaires" }, { id: "images_videos", label: "Images et vidéos" },
  { id: "affichage", label: "Affichage" }, { id: "performances", label: "Performances" },
  { id: "accessibilite", label: "Accessibilité" }, { id: "securite", label: "Sécurité" },
  { id: "seo", label: "SEO" }, { id: "fonctionnalites", label: "Fonctionnalités" },
  { id: "ecommerce", label: "E-commerce" },
];
export const WEB_ANOMALY_STATUS = {
  a_corriger: { label: "À corriger", cls: "bg-red-50 text-red-700 border-red-200" },
  en_cours: { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  a_retester: { label: "À retester", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  resolu: { label: "Résolu", cls: "bg-green-50 text-green-700 border-green-200" },
};
export const WEB_GOLIVE_CHECKLIST = [
  ["sauvegarde", "Sauvegarde effectuée"], ["dns", "DNS configurés"], ["ssl", "SSL actif"],
  ["pages_testees", "Pages testées"], ["formulaires", "Formulaires testés"], ["liens", "Liens vérifiés"],
  ["responsive", "Responsive validé"], ["seo", "SEO contrôlé"], ["sitemap", "Sitemap vérifié"],
  ["redirections", "Redirections contrôlées"], ["indexation", "Indexation vérifiée"],
  ["analytics", "Analytics vérifié"], ["confidentialite", "Confidentialité contrôlée"],
];
export const WEB_SEO_GLOBAL = [
  ["search_console", "Google Search Console"], ["analytics", "Outil analytics"],
  ["sitemap", "Sitemap XML"], ["robots_txt", "Robots.txt"], ["redirections_301", "Redirections 301"],
  ["erreurs_404", "Erreurs 404"], ["canoniques", "Balises canoniques"],
  ["donnees_structurees", "Données structurées"], ["mobile", "Optimisation mobile"],
  ["performances", "Performances"], ["local", "SEO local"],
];
export const webValidationKindLabel = (id) => WEB_VALIDATION_KINDS.find((k) => k.id === id)?.label || id;

export const serviceTypeLabel = (id) =>
  SERVICE_TYPES.find((s) => s.id === id)?.label || SERVICE_TYPES[0].label;

export const CLIENT_INTERNAL_STATUSES = [
  { id: "prospect", label: "Prospect", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "actif", label: "Client actif", cls: "bg-green-50 text-green-700 border-green-200" },
  { id: "pause", label: "Client en pause", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  { id: "ancien", label: "Ancien client", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
];

export const clientInternalStatusOf = (id) =>
  CLIENT_INTERNAL_STATUSES.find((s) => s.id === id) || CLIENT_INTERNAL_STATUSES[1];

export const CONTACT_TYPES = [
  { id: "principal", label: "Contact principal" },
  { id: "facturation", label: "Contact facturation" },
  { id: "validation", label: "Contact validation" },
  { id: "autre", label: "Autre" },
];

export const contactTypeLabel = (id) =>
  CONTACT_TYPES.find((t) => t.id === id)?.label || id;

export const PROFILE_MISSING_LABELS = {
  email: "Email de facturation",
  adresse: "Adresse postale",
  siret: "SIREN / SIRET",
};
